/**
 * Modul „Warenwirtschaft“ (Registry-Schlüssel `lager`, Pfad `/lager`) — NEO-Bestandssync #863 (ADR 007).
 * Lese-API `/api/warenwirtschaft/v1/*` (Team-Session, Cerbos `warenwirtschaft` lesen/verwalten).
 * Inbox `/api/integration/inbox/neo/warenwirtschaft` + Cursor `/api/integration/cursor/neo/warenwirtschaft`
 * (Dienstkonto-Token, tenantgebunden) — Vertrag für n8n `wf_portal_warenwirtschaft_neo_1` (Schritt B).
 * Relevanz: nur Artikel mit Bestand ≠ 0 in mind. einer Filiale werden neu geführt; einmal geführt bleiben sie bei 0
 * als „kein Bestand“ erhalten. Filiale 999 = Sonderfiliale (Bedeutung ungeklärt), wird separat gekennzeichnet.
 */

export const WAWI_API = '/api/warenwirtschaft/v1';
export const WAWI_INBOX_PFAD = '/api/integration/inbox/neo/warenwirtschaft';
export const WAWI_CURSOR_PFAD = '/api/integration/cursor/neo/warenwirtschaft';
export const WAWI_STREAM = 'neo_warenwirtschaft';
/** höchstens 500 Einträge je Inbox-Seite (NEO-Seitengröße `anzahl=500`) */
export const WAWI_BATCH_MAX = 500;
/** Standardfilialen der Tabelle; weitere (z. B. 999) erscheinen zusätzlich */
export const WAWI_FILIALEN_STANDARD = [1, 2, 3] as const;
export const WAWI_SONDERFILIALE = 999;
/** Stand gilt als veraltet, wenn der letzte veröffentlichte Snapshot älter ist (Nachtlauf 01:00 + Reserve) */
export const WAWI_VERALTET_STUNDEN = 36;

export const WAWI_BESTAND_FILTER = ['ungleich0', 'alle', 'kein', 'negativ'] as const;
export type WawiBestandFilter = (typeof WAWI_BESTAND_FILTER)[number];

/* ---------------------------------------------------------------- Inbox (n8n → Portal) */

export const WAWI_INBOX_KINDS = ['stock', 'articles', 'nachabruf', 'complete', 'fehler'] as const;
export type WawiInboxKind = (typeof WAWI_INBOX_KINDS)[number];

/** Zeile der NEO-Standard-Bestandsliste (`/bewegungsdaten/artikelbestaende`), Felder unverändert */
export interface NeoBestandZeile {
  artikelNr: number;
  filialeNr: number;
  bestand: number;
  avisiert?: number | null;
  reserviert?: number | null;
  meldemenge?: number | null;
  imWarenausgangBereitgestellt?: number | null;
  imWarenausgangDerBestellfilialeBereitgestellt?: number | null;
}

/** Artikel aus `/stammdaten/artikel/liste` bzw. `/stammdaten/artikel/{nr}` (Projektion PREISE,ZEITPUNKTE,EINSTELLUNGEN) */
export interface NeoArtikelSatz {
  artikelNr: number;
  deltaVersion: number;
  bezeichnung?: string | null;
  artikelStatus?: string | null;
  artikeltyp?: string | null;
  gtin?: string | null;
  herstellerArtikelNr?: string | null;
  referenzNr?: string | null;
  preise?: { ekPreis?: number | null; vkPreis?: number | null; uvpPreis?: number | null; sperre?: boolean | null } | null;
  einstellungen?: {
    keineBestandsfuehrung?: boolean | null;
    automatischeBestellungSperre?: boolean | null;
    filialtauschSperre?: boolean | null;
    meldemengeAutomatikSperre?: boolean | null;
  } | null;
  sortimentzuordnung?: {
    oberwarengruppeNummer?: string | null;
    oberwarengruppe?: string | null;
    warengruppeNummer?: string | null;
    warengruppe?: string | null;
    kategorie?: string | null;
    markeNummer?: number | null;
    marke?: string | null;
    submarkeNummer?: number | null;
    submarke?: string | null;
    linieNummer?: number | null;
    linie?: string | null;
    lieferant?: string | null;
    folgeArtikelGtin?: string | null;
  } | null;
  /** NEO-Datumsformat dd.MM.yyyy */
  zeitpunkte?: {
    erstlistung?: string | null;
    exDatum?: string | null;
    bestellbarVon?: string | null;
    bestellbarBis?: string | null;
    lieferbarAb?: string | null;
    nichtLieferbarSeit?: string | null;
    nichtMehrBestellenAb?: string | null;
  } | null;
}

/**
 * `POST /api/integration/inbox/neo/warenwirtschaft` — eine Seite eines Laufs. Idempotent je (runId, kind, seite):
 * gleicher Inhalt → `duplicate` mit derselben Quittung, abweichender Inhalt → 409.
 * - stock: Seite der Standard-Bestandsliste (seite 0..n-1), wird bis `complete` vorgemerkt
 * - articles: Stammdaten-Seite (seite 0..), vollständige NEO-Seite inkl. später verworfener IDs; Portal übernimmt nur
 *   geführte Artikel und Kandidaten (Bestand ≠ 0 im vorgemerkten Snapshot) und schreibt den Cursor = max(deltaVersion)
 *   aller Zeilen fort (ohne +1; leere Seite belässt ihn)
 * - nachabruf: Einzelabrufe neuer Bestands-IDs ohne Stammsatz (U); Cursor bleibt
 * - complete: prüft alle Bestandsseiten + gelöste Kandidaten und veröffentlicht den Snapshot atomar
 * - fehler: Lauf meldet Abbruch (Lease frei, Fehler sichtbar; kein Teil-Snapshot)
 */
export interface WawiInboxRequest {
  runId: string;
  kind: WawiInboxKind;
  seite: number;
  /** Abrufzeit der Bestandsliste (stock, Pflicht); bei complete optional zur Kontrolle */
  observedAt?: string;
  /** articles: Cursor, mit dem die Seite abgerufen wurde (muss dem bestätigten Portal-Cursor entsprechen) */
  cursorBefore?: number | null;
  /** stock: NeoBestandZeile[]; articles/nachabruf: NeoArtikelSatz[]; complete/fehler: [] */
  items: unknown[];
  /** complete: Anzahl Bestandsseiten und -zeilen des Snapshots */
  bestandSeiten?: number;
  bestandZeilen?: number;
  /** fehler: kurze Meldung (ohne Tokens) */
  meldung?: string;
}

export type WawiQuittungStatus = 'gespeichert' | 'duplicate' | 'veroeffentlicht' | 'fehler_erfasst';

/** Quittung = persistierte Verarbeitung; erst danach darf n8n den NEO-Cursor fortschreiben */
export interface WawiInboxQuittung {
  status: WawiQuittungStatus;
  runId: string;
  kind: WawiInboxKind;
  seite: number;
  anzahl: number;
  /** articles/nachabruf: übernommen = geführt oder Kandidat, verworfen = bewusst nicht geführt */
  uebernommen?: number;
  verworfen?: number;
  /** stock: Zeilen mit Bestand ≠ 0 (Kandidaten) */
  kandidaten?: number;
  /** bestätigter Stamm-Cursor nach dieser Seite */
  artikelCursor: number | null;
  /** complete */
  snapshotId?: string;
  artikelMitBestand?: number;
  nullgesetzt?: number;
  keinBestandNeu?: number;
  quittiertAt: string;
}

/** Fehler der Inbox: 400 Schema, 401 Token, 409 Konflikt/Lease/Cursor/veraltet/Kandidaten offen, 413 > 500, 503 nicht konfiguriert */
export interface WawiInboxFehler {
  statusCode: number;
  code: string;
  message: string;
  /** complete mit offenen Kandidaten: Artikelnummern ohne Stammsatz */
  offen?: number[];
}

/** `GET /api/integration/cursor/neo/warenwirtschaft` */
export interface WawiCursor {
  stream: string;
  /** nächste Stammseite mit `version=<artikelCursor>` (exklusiv, ohne +1); null = Bootstrap ab version=0 */
  artikelCursor: number | null;
  /** Abrufzeit des zuletzt veröffentlichten Bestands-Snapshots */
  snapshotAt: string | null;
  lastSuccessAt: string | null;
  /** unvollstaendig = Abschluss abgelehnt (Seiten/Kandidaten fehlen), alter Snapshot bleibt aktiv (Review 221) */
  phase: 'bereit' | 'laeuft' | 'unvollstaendig' | 'fehler';
  runId: string | null;
  lockedUntil: string | null;
  /** aufeinanderfolgende abgelehnte Abschlüsse; 0 nach Erfolg */
  fehlerZaehler: number;
  /** Fortschritt des aktuellen Laufs; zurKlaerung = offene Kandidaten ohne Stammsatz aus dem letzten abgelehnten Abschluss */
  checkpoint: { bestandSeiten?: number; artikelSeite?: number; zurKlaerung?: number[] };
}

/* ---------------------------------------------------------------- Lese-API (Team) */

export interface WawiFilialBestand {
  filialNr: number;
  bestand: number;
  sonderfiliale: boolean;
}

export interface WawiArtikelKurz {
  artikelNr: number;
  bezeichnung: string | null;
  marke: string | null;
  oberNr: string | null;
  oberName: string | null;
  wgNr: string | null;
  wgName: string | null;
  bestaende: WawiFilialBestand[];
  /** in keiner Filiale Bestand ≠ 0 (Nullübergang) */
  keinBestand: boolean;
  keinBestandSeit: string | null;
  negativ: boolean;
}

export interface WawiStand {
  snapshotAt: string | null;
  lastSuccessAt: string | null;
  veraltet: boolean;
}

/** `GET /api/warenwirtschaft/v1/artikel?suche=&filiale=&ober=&wg=&zuordnung=ohne&bestand=ungleich0|alle|kein|negativ&seite=&groesse=` */
export interface WawiArtikelListe {
  eintraege: WawiArtikelKurz[];
  gesamt: number;
  seite: number;
  groesse: number;
  /** alle Filialnummern im Bestand (für Spalten/Filter), aufsteigend */
  filialen: number[];
  stand: WawiStand;
}

export interface WawiFilialBestandDetail extends WawiFilialBestand {
  avisiert: number;
  reserviert: number;
  meldemenge: number;
  ausgang: number;
  ausgangBestellfiliale: number;
  observedAt: string;
}

/** `GET /api/warenwirtschaft/v1/artikel/:nr` */
export interface WawiArtikel extends Omit<WawiArtikelKurz, 'bestaende'> {
  deltaVersion: number;
  artikelStatus: string | null;
  artikeltyp: string | null;
  gtin: string | null;
  herstellerArtikelNr: string | null;
  referenzNr: string | null;
  kategorie: string | null;
  submarke: string | null;
  linie: string | null;
  lieferant: string | null;
  ekPreis: number | null;
  vkPreis: number | null;
  uvpPreis: number | null;
  preisSperre: boolean | null;
  keineBestandsfuehrung: boolean | null;
  erstlistung: string | null;
  exDatum: string | null;
  firstSeenAt: string;
  sourceSeenAt: string;
  bestaende: WawiFilialBestandDetail[];
  stand: WawiStand;
}

export interface WawiFilialAggregat {
  filialNr: number;
  summe: number;
  positiv: number;
  negativ: number;
  /** Artikel mit Zeile in dieser Filiale, aber Bestand 0 */
  null: number;
}

export interface WawiWarengruppe {
  wgNr: string | null;
  wgName: string | null;
  artikel: number;
  keinBestand: number;
  filialen: WawiFilialAggregat[];
}

export interface WawiOberwarengruppe {
  /** null = „Nicht zugeordnet“ */
  oberNr: string | null;
  oberName: string | null;
  artikel: number;
  keinBestand: number;
  filialen: WawiFilialAggregat[];
  gruppen: WawiWarengruppe[];
}

/** `GET /api/warenwirtschaft/v1/warengruppen` */
export interface WawiWarengruppen {
  baum: WawiOberwarengruppe[];
  filialen: number[];
  stand: WawiStand;
}

/** `GET /api/warenwirtschaft/v1/sync` (Cerbos warenwirtschaft verwalten) */
export interface WawiSync extends WawiCursor {
  lastError: string | null;
  lastErrorAt: string | null;
  snapshotId: string | null;
  artikel: number;
  keinBestand: number;
  veraltet: boolean;
}
